define('threadsThreadView',[
  'app',
  'module',
  'backbone',
  'marionette',
  'underscore',
  'settings',
  'dateUtils',
  'threadsThreadMessageView',
  'threadsThreadDraftView',
  'threadsThreadReplyView',
  'mailsMailHeaderLabelsView',
  'template!threadsThreadView',
  'entities/threads'
], function (
  App,
  Module,
  Backbone,
  Marionette,
  _,
  Settings,
  DateUtils,
  MessageView,
  DraftView,
  ReplyView,
  LabelsHeaderView,
  Tpl
) {
  'use strict';

  Module.exports = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'thread-detail-view',

    regions: {
      labelsRegion: '.thread-labels-region',
      replyRegion: '.thread-reply-region'
    },

    ui: {
      messagesContainer: '.thread-messages',
      btnTrash: '.btn-trash',
      btnMarkRead: '.btn-mark-read',
      btnBack: '.btn-back'
    },

    events: {
      'click @ui.btnTrash': 'onTrash',
      'click @ui.btnMarkRead': 'onToggleRead',
      'click @ui.btnBack': 'onBack'
    },

    serializeData: function() {
      return {
        subject: this.model.get('subject') || '(no subject)'
      };
    },

    onShow: function() {
      this.fetch();
    },

    fetch: function() {
      if (this.fetchRequest) {
        this.fetchRequest.abort();
      }

      this.ui.messagesContainer.html('<div class="loader">Loading...</div>');

      this.fetchRequest = $.ajax({
        type: 'GET',
        url: App.request('threads:get-thread-url', this.options.mailSecId, this.model.get('threadId')),
        success: _.bind(function (thread) {
          // The user can navigate away while the fetch is in flight — Marionette
          // destroys the view and the bound `this.ui.*` refs become plain
          // selector strings, so calling .html()/.empty() on them throws.
          // isDestroyed is set by Marionette in destroy(); bail early if so.
          if (this.isDestroyed) { return; }
          if (thread && thread.messages) {
            // Remember the mailbox address (used to avoid Cc'ing ourselves
            // when pre-filling the reply Cc).
            this.mailboxEmail = (thread.emailAddress && thread.emailAddress.email) || this.mailboxEmail;
            // Separate real messages from drafts
            var realMessages = [];
            var draftMessages = [];

            _.each(thread.messages, function(message) {
              if (message.draft) {
                draftMessages.push(message);
              } else {
                realMessages.push(message);
              }
            });

            // Render real messages as the conversation. Kept on the view so
            // cancelling a forward can restore the reply composer without a
            // refetch.
            this.realMessages = realMessages;
            this.renderMessages(realMessages);

            // Render labels with add/remove support
            this.renderLabels(thread);

            // Render the draft (if any) or the reply composer in the compose area
            if (draftMessages.length > 0) {
              var draft = draftMessages[draftMessages.length - 1];
              this.renderDraftComposer(draft);
            } else {
              // Reply to the most recent message that we did NOT send — i.e.
              // the customer's latest email. Replying to the first message is
              // wrong when WE started the thread (first message is ours), and
              // replying to the literal last message is wrong when our own
              // reply is the most recent. Picking the last inbound message
              // gets the right correspondent in both cases.
              this.renderReplyComposer(this.pickReplyTarget(realMessages));
            }
          }
        }, this),
        error: _.bind(function(xhr, textStatus) {
          if (this.isDestroyed || textStatus === 'abort') { return; }
          this.ui.messagesContainer.html('<div class="error">Failed to load thread</div>');
        }, this)
      });
    },

    // Cancel the in-flight thread fetch when the view is torn down so its
    // callback doesn't try to touch a detached DOM.
    onBeforeDestroy: function() {
      if (this.fetchRequest) {
        this.fetchRequest.abort();
        this.fetchRequest = null;
      }
    },

    renderMessages: function(messages) {
      this.ui.messagesContainer.empty();
      this.lastMessageEl = null;
      this.linkedMessageEl = null;
      this.scrollTargetEl = null;
      this.scrollSpacer = null;

      _.each(messages, _.bind(function(message, index) {
        var isLast = (index === messages.length - 1);
        var messageModel = new Backbone.Model(message);

        var messageView = new MessageView({
          model: messageModel,
          expanded: isLast,
          // Pass the mailbox secId so each message can render its attachments
          // via the shared email-attachment view (preview + download).
          mailSecId: this.options.mailSecId,
          // Outlines the specific message this thread was opened for (e.g. the
          // sales-inbox item's linked email). Optional — absent on the plain
          // Threads page, so nothing is highlighted there.
          highlightMessageId: this.options.highlightMessageId,
          // Optional (inbox item): () => promise of the offers/orders an
          // attachment can be filed into, for the attachments' context menu.
          attachmentTargets: this.options.attachmentTargets
        });
        messageView.render();
        messageView.on('message:forward', _.bind(this.onForwardMessage, this));
        this.ui.messagesContainer.append(messageView.$el);
        if (isLast) { this.lastMessageEl = messageView.$el[0]; }
        if (this.options.highlightMessageId && message.id === this.options.highlightMessageId) {
          this.linkedMessageEl = messageView.$el[0];
        }
        // onShow must be triggered manually for dynamically appended views so
        // the attachments region can show its child view.
        if (typeof messageView.triggerMethod === 'function') {
          messageView.triggerMethod('show');
        }
      }, this));

      // Opt-in (e.g. the Sales Inbox embed): land on the email the thread was
      // opened for — the linked message when we know which it is, otherwise the
      // most recent one — so the rep sees what they need rather than the top of
      // a long thread. Alignment re-runs as late content (images, attachment
      // regions) lays out. The standalone Threads page passes neither flag, so
      // its scroll position is unchanged.
      this.scrollTargetEl = this.linkedMessageEl || this.lastMessageEl;
      if ((this.options.scrollToLatest || this.linkedMessageEl) && this.scrollTargetEl) {
        // The spacer below the last message guarantees room to scroll its top
        // to the top of the area even when it's short. A linked message in the
        // middle of the thread already has later messages below it, so it needs
        // no spacer.
        if (this.scrollTargetEl === this.lastMessageEl) {
          this.scrollSpacer = document.createElement('div');
          this.scrollSpacer.className = 'thread-scroll-spacer';
          this.ui.messagesContainer.append(this.scrollSpacer);
        }
        this.scheduleScrollToLast();
      }
    },

    // Align the last email's top with the top of the scroll area, retrying as
    // layout settles (fonts, async attachment regions and images all change
    // height after the initial render and would otherwise leave the target
    // mis-positioned).
    scheduleScrollToLast: function() {
      var raf = window.requestAnimationFrame || function (fn) { return window.setTimeout(fn, 16); };
      raf(_.bind(this.scrollToLast, this));
      // Backstop for content that lays out after the first frame.
      window.setTimeout(_.bind(this.scrollToLast, this), 250);
      // Re-align when images inside the thread finish loading.
      var container = this.ui.messagesContainer && this.ui.messagesContainer[0];
      if (container) {
        var imgs = container.getElementsByTagName('img');
        for (var i = 0; i < imgs.length; i++) {
          if (!imgs[i].complete) {
            $(imgs[i]).one('load', _.bind(this.scrollToLast, this));
          }
        }
      }
    },

    // Put the beginning of the last email at the top of the scroll area. The
    // spacer (sized here) provides the scroll room; rects (not offsetTop) make
    // the alignment correct whatever the container's positioning.
    scrollToLast: function() {
      var target = this.scrollTargetEl || this.lastMessageEl;
      if (this.isDestroyed || !target) { return; }
      var container = this.ui.messagesContainer && this.ui.messagesContainer[0];
      if (!container || typeof container.getBoundingClientRect !== 'function') { return; }
      if (this.scrollSpacer) {
        var room = container.clientHeight - target.offsetHeight - 8;
        this.scrollSpacer.style.height = (room > 0 ? room : 0) + 'px';
      }
      var c = container.getBoundingClientRect();
      var l = target.getBoundingClientRect();
      container.scrollTop += (l.top - c.top);
    },

    renderLabels: function(thread) {
      // Create a model that wraps thread labels and provides
      // addLabel/removeLabel methods compatible with the mail labels view
      var mailSecId = this.options.mailSecId;
      var threadId = this.model.get('threadId');
      var self = this;

      this.labelsModel = new Backbone.Model({
        labels: thread.labels || [],
        id: threadId
      });

      // Adapt the mail-labels view's (secId, folder, label) signature to the
      // thread API's (mailSecId, label) and delegate. We deliberately do NOT
      // mutate labelsModel.labels here: the shared mails views (AddView,
      // LabelsHeaderView) already update the labels array on success. Mutating
      // it again here pushed every added label twice → it rendered twice.
      this.labelsModel.addLabel = function(secId, folder, label) {
        return self.model.addLabel(mailSecId, label);
      };

      this.labelsModel.removeLabel = function(secId, label) {
        return self.model.removeLabel(mailSecId, label);
      };

      this.labelsHeaderView = new LabelsHeaderView({
        model: this.labelsModel,
        mailSecId: mailSecId
      });
      this.getRegion('labelsRegion').show(this.labelsHeaderView);
    },

    renderDraftComposer: function(draftMessage) {
      var draftModel = new Backbone.Model(draftMessage);
      this.draftView = new DraftView({
        model: draftModel,
        threadId: this.model.get('threadId'),
        mailSecId: this.options.mailSecId,
        // The sales / support inbox item this thread is shown for, if any
        // ("notify me of a reply" keeps it on the watch).
        salesInboxItemSecId: this.options.salesInboxItemSecId,
        // Start the composer minimised when the host asks (Sales Inbox embed)
        // so the conversation gets the vertical space; click the bar to open.
        collapsible: this.options.collapsibleReply,
        collapsed: this.options.collapsibleReply,
        // Our own mailbox address — used to drop ourselves ("from") from the
        // pre-filled To/Cc so the draft never replies back to our own inbox.
        mailbox: { email: this.mailboxEmail },
        parentView: this
      });
      this.getRegion('replyRegion').show(this.draftView);
      this.draftView.on('draft:sent', _.bind(function() { this.fetch(); }, this));
      this.draftView.on('draft:discarded', _.bind(function() { this.fetch(); }, this));
    },

    // True when a message was sent by us (outbound) rather than received.
    // Gmail tags our own messages with the SENT system label; some mailboxes
    // also expose it via the folder name. Match either, case-insensitively.
    isSentMessage: function(message) {
      if (!message) { return false; }
      var hit = function(name) { return (name || '').toUpperCase().indexOf('SENT') !== -1; };
      if (message.folder && hit(message.folder.id || message.folder.name)) { return true; }
      if (message.labels && message.labels.length) {
        return _.some(message.labels, function(l) {
          return l && hit(l.id || l.name);
        });
      }
      return false;
    },

    // Choose which message the reply composer targets: the last message in the
    // thread that we did NOT send (the latest inbound/customer email). Falls
    // back to the literal last message when every message looks outbound (e.g.
    // labels missing), then to the first — so we always have a target.
    pickReplyTarget: function(messages) {
      if (!messages || !messages.length) { return null; }
      for (var i = messages.length - 1; i >= 0; i--) {
        if (!this.isSentMessage(messages[i])) { return messages[i]; }
      }
      return messages[messages.length - 1];
    },

    // Swap the compose area to a forward composer targeting that message. The
    // composer opens expanded with empty recipients and the quoted original;
    // cancelling restores the normal reply composer from the kept messages.
    onForwardMessage: function(messageModel) {
      this.forwardView = new ReplyView({
        threadId: this.model.get('threadId'),
        mailSecId: this.options.mailSecId,
        // The sales / support inbox item this thread is shown for, if any
        // ("notify me of a reply" keeps it on the watch).
        salesInboxItemSecId: this.options.salesInboxItemSecId,
        firstMessage: messageModel.toJSON(),
        forwardMessage: messageModel.toJSON(),
        collapsible: false,
        collapsed: false,
        mailbox: { email: this.mailboxEmail },
        templateLocale: this.options.templateLocale,
        customerLocale: this.options.customerLocale,
        customerLinked: this.options.customerLinked,
        customerEndUserId: this.options.customerEndUserId,
        // Signature template type (default MAIL_SIGN; the support inbox asks
        // for MAIL_SIGN_SUPPORT).
        signatureType: this.options.signatureType,
        parentView: this
      });
      this.getRegion('replyRegion').show(this.forwardView);
      this.forwardView.on('reply:sent', _.bind(function() { this.fetch(); }, this));
      this.forwardView.on('forward:cancel', _.bind(function() {
        this.renderReplyComposer(this.pickReplyTarget(this.realMessages || []));
      }, this));
      // Bring the composer into view so the action has visible feedback.
      var el = this.forwardView.$el && this.forwardView.$el[0];
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest' });
      }
    },

    renderReplyComposer: function(firstMessage) {
      this.replyView = new ReplyView({
        threadId: this.model.get('threadId'),
        mailSecId: this.options.mailSecId,
        // The sales / support inbox item this thread is shown for, if any
        // ("notify me of a reply" keeps it on the watch).
        salesInboxItemSecId: this.options.salesInboxItemSecId,
        firstMessage: firstMessage,
        // Start the composer minimised when the host asks (Sales Inbox embed)
        // so the conversation gets the vertical space; click the bar to open.
        collapsible: this.options.collapsibleReply,
        collapsed: this.options.collapsibleReply,
        // Our own mailbox address — used to drop ourselves from the pre-filled Cc.
        mailbox: { email: this.mailboxEmail },
        // Locale used to pick the signature language (optional — the reply
        // composer falls back to the operator's UI language / English).
        templateLocale: this.options.templateLocale,
        // Customer's own language — drives the From sender-address selection.
        customerLocale: this.options.customerLocale,
        // When no customer is linked the composer asks which signature
        // language to use rather than guessing from the email locale.
        customerLinked: this.options.customerLinked,
        // The linked customer's endUserId — lets the reply composer load the
        // customer so the signature template can use customer.* variables.
        customerEndUserId: this.options.customerEndUserId,
        // Signature template type (default MAIL_SIGN; the support inbox asks
        // for MAIL_SIGN_SUPPORT).
        signatureType: this.options.signatureType,
        parentView: this
      });
      this.getRegion('replyRegion').show(this.replyView);
      this.replyView.on('reply:sent', _.bind(function() {
        this.fetch();
      }, this));
    },

    onTrash: function() {
      this.model.trash(this.options.mailSecId).done(_.bind(function() {
        this.onBack();
      }, this));
    },

    onToggleRead: function() {
      if (this.model.get('hasUnread')) {
        this.model.markRead(this.options.mailSecId);
        this.model.set('hasUnread', false);
      } else {
        this.model.markUnread(this.options.mailSecId);
        this.model.set('hasUnread', true);
      }
    },

    onBack: function() {
      this.$el.parent().empty();
      if (this.options.parentView) {
        this.options.parentView.refresh();
      }
    }
  });
});

